import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
// Component & Asset Imports
import Toptext from '../../components/Toptext';
import Globe from '../../assets/icons/globe.svg';
import DynamicHeading from '../../components/ui/CustomHeading';

// Swiper Imports
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation, Thumbs, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';

// Lucide React Icon Imports
import { Box, Ruler, SquareArrowOutUpRight, BatteryCharging, Gauge, Radar, Code, Settings2, Terminal, Smartphone, FlaskConical, Bolt, Wifi, ChartCandlestickIcon, AppWindowMacIcon, Layers3, CheckCircle2, ChevronDown, Eye, Car } from 'lucide-react';

// Lightbox
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

// Other Icon Imports
import { BiSupport, BiDownload } from 'react-icons/bi';
import { FaRulerCombined } from 'react-icons/fa';
import Button5 from '../../components/ui/Button5';

gsap.registerPlugin(ScrollTrigger);

// --- FaqItem Component (Unchanged) ---
const FaqItem = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="border-b border-gray-200 py-6">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex justify-between items-center text-left text-lg font-semibold text-gray-800 hover:text-jbsBlue focus:outline-none"
            >
                <span>{question}</span>
                <ChevronDown className={`w-6 h-6 transform transition-transform duration-300 ${isOpen ? 'rotate-180 text-jbsBlue' : ''}`} />
            </button>
            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-96 mt-4' : 'max-h-0'}`}>
                <p className="text-jbsGray pt-2">{answer}</p>
            </div>
        </div>
    );
};

// --- Icon Map (Unchanged) ---
const IconMap = {
    package: <Box className="w-8 h-8 text-jbsBlue" />,
    ruler: <Ruler className="w-8 h-8 text-jbsBlue" />,
    batteryCharging: <BatteryCharging className="w-8 h-8 text-jbsBlue" />,
    gauge: <Gauge className="w-8 h-8 text-jbsBlue" />,
    radar: <Radar className="w-8 h-8 text-jbsBlue" />,
    code: <Code className="w-8 h-8 text-jbsBlue" />,
    dimensions: <FaRulerCombined className="w-8 h-8 text-jbsBlue" />,
    settings: <Settings2 className="w-8 h-8 text-jbsBlue" />,
    default: <BiSupport className="w-8 h-8 text-jbsBlue" />,
    monitor: <Terminal className="w-8 h-8 text-jbsBlue" />,
    smartphone: <Smartphone className="w-8 h-8 text-jbsBlue" />,
    flask: <FlaskConical className="w-8 h-8 text-jbsBlue" />,
    bolt: <Bolt className="w-8 h-8 text-jbsBlue" />,
    wifi: <Wifi className="w-8 h-8 text-jbsBlue" />,
    chartLine: <ChartCandlestickIcon className="w-8 h-8 text-jbsBlue" />,
    api: <AppWindowMacIcon className="w-8 h-8 text-jbsBlue" />,
    layers: <Layers3 className="w-8 h-8 text-jbsBlue" />,
    eye: <Eye className="w-8 h-8 text-jbsBlue" />,
    car: <Car className="w-8 h-8 text-jbsBlue" />
};

const ProductServices = ({
    topText,
    mainHeadingParts,
    subText,
    heroImage,
    button1Type,
    button1Text,
    button1TextDescription,
    button2Type,
    variants,
    specs,
    productImages,
    brochureLink,
    CTAHeading,
    offers,
    CTASubHeading,
    CTALink,
    CTAText,
    section2HeadingParts,
    section2Image,
    type,
    pricingPlans = [],
    trustedByLogos = [],
    faqs = []
}) => {
    const mainRef = useRef(null);
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const location = useLocation();
    const navigate = useNavigate();


    useEffect(() => {
        const mainTitle = mainHeadingParts.map(p => p.text).join(' ')
        document.title = `${mainTitle} | JBS Capacitors`;
            window.scrollTo(0, 0);
        // }
    }, [location.pathname,mainHeadingParts]);

    useGSAP(() => {
        // Hero Section Intro
        const tl = gsap.timeline({ defaults: { ease: 'power3.out', duration: 0.8 } });
        tl.from('.anim-toptext', { y: -30, opacity: 0 })
          .from('.anim-main-heading span', { y: 40, opacity: 0, stagger: 0.1 }, "-=0.5")
          .from('.anim-subtext', { y: 20, opacity: 0 }, "-=0.6")
          .from('.anim-hero-image', { scale: 1.1, opacity: 0, duration: 1.2 }, "-=0.8")
          .from('.anim-hero-cta', { x: 50, opacity: 0, stagger: 0.2 , onComplete: () => {
            gsap.set('.anim-hero-cta', { clearProps: 'all' });
          }}, "-=1")
         
        // General scroll-triggered animation for headings and sections
        const animateOnScroll = (selector) => {
            gsap.from(selector, {
                y: 50,
                opacity: 0,
                duration: 1,
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: selector,
                    start: 'top 85%',
                    toggleActions: 'play none none none'
                }
            });
        };

        ['.anim-offers-section', '.anim-pricing-section', '.anim-variants-section', '.anim-specs-section', '.anim-trusted-section', '.anim-gallery-section', '.anim-faq-section', '.anim-secondary-section-heading', '.anim-secondary-section-image'].forEach(sel => {
            const el = document.querySelector(sel);
            if (el) animateOnScroll(el);
        });

        // Special animation for the final CTA cards
        gsap.from('.anim-cta-brochure', { x: -100, opacity: 0, scrollTrigger: { trigger: '.anim-cta-brochure', start: 'top 90%' } });
        gsap.from('.anim-cta-contact', { x: 100, opacity: 0, scrollTrigger: { trigger: '.anim-cta-contact', start: 'top 90%' } });
    }, { scope: mainRef,dependencies: [location.pathname]  });

    // Framer Motion variants for grid items
    const gridContainerVariants = {
        hidden: {},
        visible: { transition: { staggerChildren: 0.1 } }
    };

    const gridItemVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100 } }
    };

    const openLightbox = (index) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    return (
        <>
            <div ref={mainRef} className='mt-32 flex flex-col items-center justify-center px-4'>
                <div className="anim-toptext">
                    <Toptext icon={Globe}>{topText}</Toptext>
                </div>
                <div className='mt-12 mx-auto text-center'>
                    <div className="anim-main-heading">
                        <DynamicHeading parts={mainHeadingParts} />
                    </div>
                    <p className='anim-subtext font-light text-jbsGray mt-4 max-w-3xl mx-auto text-lg md:text-xl'>{subText}</p>
                    <div className="flex flex-col md:flex-row w-full h-full gap-4 mt-12">
                        <div className="anim-hero-image md:w-[70%] w-full h-64 md:h-auto">
                            <img src={heroImage} alt="Product Hero" className="w-full h-full object-cover rounded-2xl" />
                        </div>
                        <div className="md:w-[30%] w-full flex flex-col gap-4">
                            <div className="anim-hero-cta border-[3px] border-jbsBlue rounded-[38px] shadow p-6 flex-1 flex flex-col">
                                {button1Type === 'price' && (
                                    <div className="mt-auto text-left">
                                        <h3 className="text-sm text-jbsBlue font-semibold">Starts from</h3>
                                        <p className="text-4xl font-bold leading-none tracking-tighter text-jbsBlue">{button1Text}</p>
                                        <p className="text-sm font-bold text-jbsBlue">{button1TextDescription}</p>
                                    </div>
                                )}
                            </div>
                            <a href="#enquiry" className="anim-hero-cta bg-jbsBlue text-white rounded-[38px] shadow p-6 flex-1 flex flex-col hover:bg-opacity-90 transition-all duration-300">
                                {button2Type === 'enquiry' && (
                                    <div className="mt-auto flex items-end justify-between text-left">
                                        <h3 className="text-[28px] sm:text-[36px] md:text-[30px] lg:text-[38px] tracking-[-1px] md:tracking-[-1.5px] lg:tracking-[-2px] leading-[36px] sm:leading-[42px] md:leading-[46px] lg:leading-[50px] font-bold">Place an Enquiry</h3>
                                        <BiSupport className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 text-white" />
                                    </div>
                                )}
                                {button2Type === 'visit' && (
                                    <div className="mt-auto flex items-end justify-between text-left">
                                        <h3 className="text-3xl leading-8 text-white font-bold">Visit Product Website</h3>
                                        <SquareArrowOutUpRight className='w-14 h-14 text-white' />
                                    </div>
                                )}
                            </a>
                        </div>
                    </div>
                </div>

                {offers && offers.length > 0 && (
                    <div className='w-full max-w-6xl mx-auto mt-24 text-center anim-offers-section'>
                        <DynamicHeading parts={[{ text: "What We " }, { text: "Offer", highlight: true }]} />
                        <motion.div variants={gridContainerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {offers.map((offer, index) => (
                                <motion.div key={index} variants={gridItemVariants} className="bg-gray-50 rounded-2xl p-6 text-left flex items-start gap-5 border border-gray-200 shadow-sm hover:shadow-xl hover:border-jbsBlue/50 hover:-translate-y-2 transition-all duration-300">
                                    <div className="flex-shrink-0 mt-1">
                                        {IconMap[offer.icon] || IconMap.default}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-xl text-gray-800">{offer.title}</h4>
                                        <p className="text-jbsGray mt-1">{offer.description}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                )}

                {type === 'trading' && pricingPlans && pricingPlans.length > 0 && (
                    <div className='w-full max-w-6xl mx-auto mt-24 text-center anim-pricing-section'>
                        <DynamicHeading parts={[{ text: "Flexible " }, { text: "Pricing", highlight: true }, { text: " Plans" }]} />
                        <p className="font-light text-jbsGray mt-4 max-w-2xl mx-auto text-lg">Choose the perfect plan that fits your trading needs. No hidden fees.</p>
                        <motion.div variants={gridContainerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-start">
                            {pricingPlans.map((plan, index) => (
                                <motion.div key={index} variants={gridItemVariants} className="h-full">
                                    <div className={`relative rounded-2xl p-8 text-left border shadow-sm flex flex-col h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-2 ${plan.featured ? 'bg-jbsBlue text-white border-jbsBlue' : 'bg-gray-50 border-gray-200'}`}>
                                        {plan.featured && (
                                            <div className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2">
                                                <span className="bg-pink-500 text-white text-xs font-bold px-4 py-1 rounded-full uppercase">Most Popular</span>
                                            </div>
                                        )}
                                        <h3 className={`text-2xl font-bold ${plan.featured ? 'text-white' : 'text-gray-800'}`}>{plan.title}</h3>
                                        <p className={`mt-2 ${plan.featured ? 'text-blue-200' : 'text-jbsGray'}`}>{plan.description}</p>
                                        <div className="my-8">
                                            <span className={`text-5xl font-extrabold ${plan.featured ? 'text-white' : 'text-jbsBlue'}`}>{plan.price}</span>
                                            <span className={`text-lg font-semibold ml-1 ${plan.featured ? 'text-blue-200' : 'text-jbsGray'}`}>{plan.frequency}</span>
                                        </div>
                                        <ul className="space-y-4 mb-8">
                                            {plan.features.map((feature, i) => (
                                                <li key={i} className="flex items-center gap-3">
                                                    <CheckCircle2 className={`w-5 h-5 flex-shrink-0 ${plan.featured ? 'text-green-300' : 'text-jbsBlue'}`} />
                                                    <span>{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <button className={`w-full mt-auto font-bold py-3 px-6 rounded-lg transition-colors duration-300 ${plan.featured ? 'bg-white text-jbsBlue hover:bg-blue-100' : 'bg-jbsBlue text-white hover:bg-opacity-90'}`}>
                                            {plan.ctaText}
                                        </button>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                )}

                {variants && variants.length > 0 && (
                    <div className='w-full max-w-6xl mx-auto mt-24 text-center anim-variants-section'>
                        <DynamicHeading parts={[{ text: "Product" }, { text: "Variants", highlight: true }]} />
                        <motion.div variants={gridContainerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {variants.map((variant, index) => (
                                <motion.div key={index} variants={gridItemVariants} className="bg-gray-50 rounded-2xl p-6 text-left flex items-start gap-5 border border-gray-200 shadow-sm hover:shadow-xl hover:border-jbsBlue/50 hover:-translate-y-2 transition-all duration-300">
                                    <div className="flex-shrink-0 mt-1">
                                        {IconMap[variant.icon] || IconMap.default}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-xl text-gray-800">{variant.title}</h4>
                                        <p className="text-jbsGray mt-1">{variant.description}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                )}

                {specs && specs.length > 0 && (
                    <div className='w-full max-w-6xl mx-auto mt-24 text-center anim-specs-section'>
                        <DynamicHeading parts={[{ text: "Key" }, { text: "Specs", highlight: true }]} />
                        <motion.div variants={gridContainerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {specs.map((spec, index) => (
                                <motion.div key={index} variants={gridItemVariants} className="bg-gray-50 rounded-2xl p-6 text-left flex items-start gap-5 border border-gray-200 shadow-sm hover:shadow-xl hover:border-jbsBlue/50 hover:-translate-y-2 transition-all duration-300">
                                    <div className="flex-shrink-0 mt-1">
                                        {IconMap[spec.icon] || IconMap.default}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-xl text-gray-800">{spec.title}</h4>
                                        <p className="text-jbsGray mt-1">{spec.description}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                )}

                {type === 'trading' && trustedByLogos && trustedByLogos.length > 0 && (
                    <div className='w-full max-w-6xl mx-auto mt-24 text-center anim-trusted-section'>
                        <h3 className="text-xl font-semibold text-jbsGray tracking-wider uppercase">Trusted by leading companies worldwide</h3>
                        <div className="mt-10 flex flex-wrap justify-center items-center gap-x-12 gap-y-8">
                            {trustedByLogos.map((logo, index) => (
                                <img
                                    key={index}
                                    src={logo.src}
                                    alt={logo.alt}
                                    className="h-10 w-auto filter grayscale hover:filter-none transition-all duration-300"
                                />
                            ))}
                        </div>
                    </div>
                )}

                {productImages && productImages.length > 0 && (
                    <div className='w-full max-w-6xl mx-auto mt-24 text-center anim-gallery-section'>
                        <DynamicHeading parts={[{ text: "Product" }, { text: "Gallery", highlight: true }]} />
                        <div className="mt-12 flex flex-col lg:flex-row gap-4">
                            <div className="lg:w-3/4 w-full h-[500px]">
                                <Swiper loop={true} spaceBetween={10} navigation={true} autoplay={{ delay: 3000, disableOnInteraction: false }} thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }} modules={[FreeMode, Navigation, Thumbs, Autoplay]} className="h-full rounded-lg">
                                    {productImages.map((img, index) => (
                                        <SwiperSlide key={index} onClick={() => openLightbox(index)} className="cursor-pointer">
                                            <img src={img} alt={`Product View ${index + 1}`} className="w-full h-full object-cover" />
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </div>
                            <div className="lg:w-1/4 w-full h-[120px] lg:h-[500px]">
                                <Swiper onSwiper={setThumbsSwiper} loop={true} spaceBetween={10} direction="horizontal" slidesPerView={4} freeMode={true} watchSlidesProgress={true} modules={[FreeMode, Navigation, Thumbs]} className="h-full" breakpoints={{ 1024: { direction: "vertical" } }}>
                                    {productImages.map((img, index) => (
                                        <SwiperSlide key={index} className="cursor-pointer rounded-lg overflow-hidden opacity-60 hover:opacity-100 transition-all duration-300 [&.swiper-slide-thumb-active]:opacity-100 [&.swiper-slide-thumb-active]:border-2 [&.swiper-slide-thumb-active]:border-jbsBlue">
                                            <img src={img} alt={`Product Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </div>
                        </div>
                    </div>
                )}

                {type === 'trading' && faqs && faqs.length > 0 && (
                    <div className='w-full max-w-4xl mx-auto mt-24 text-center anim-faq-section'>
                        <DynamicHeading parts={[{ text: "Frequently Asked " }, { text: "Questions", highlight: true }]} />
                        <motion.div variants={gridContainerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} className="mt-12 text-left">
                            {faqs.map((faq, index) => (
                                <motion.div key={index} variants={gridItemVariants}>
                                    <FaqItem question={faq.question} answer={faq.answer} />
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                )}

                <div className="w-full max-w-6xl mx-auto mt-24 flex flex-col md:flex-row gap-4 md:gap-6 h-auto md:h-60">
                    <div className="anim-cta-brochure w-full rounded-[38px] md:w-1/4 bg-jbsBlue text-white p-6 flex flex-col border border-gray-200 min-h-40">
                        <div className="mt-auto flex items-center justify-between gap-3">
                            <a href={brochureLink} download className="font-bold text-[28px] leading-tight sm:text-[24px] md:text-[28px] transition-colors">Download Brochure</a>
                            <BiDownload className="w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20" />
                        </div>
                    </div>
                    <div className="anim-cta-contact w-full md:w-3/4 border-4 border-jbsBlue text-jbsBlue rounded-[38px] p-6 flex flex-col justify-between h-auto md:h-60">
                        <div className="flex flex-col md:flex-row items-start md:items-end justify-between w-full gap-4 md:gap-6">
                            <div className="text-left">
                                <h3 className="text-[28px] sm:text-[36px] md:text-[38px] lg:text-[38px] tracking-[-1px] md:tracking-[-1.5px] lg:tracking-[-2px] leading-[36px] sm:leading-[42px] md:leading-[46px] lg:leading-[50px] font-bold">{CTAHeading}</h3>
                            </div>
                            <Button5 onClick={()=>{navigate("/contact")}} className="w-full sm:w-auto md:w-[300px] lg:w-[400px]">CONTACT NOW</Button5>
                        </div>
                    </div>
                </div>

                {section2HeadingParts && section2HeadingParts.length > 0 && (
                    <div className='flex mt-24 mx-auto text-center anim-secondary-section-heading'>
                        <DynamicHeading parts={section2HeadingParts} />
                    </div>
                )}

                {section2Image && (
                    <div className="mt-24 anim-secondary-section-image">
                        <img src={section2Image} alt="Secondary Section Visual" className="w-full max-w-6xl mx-auto rounded-2xl" />
                    </div>
                )}
            </div>

            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={productImages.map(img => ({ src: img }))}
                index={lightboxIndex}
            />
        </>
    );
};

export default ProductServices;
